import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const EditorContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;

/* ── Default section definitions for HomePage ── */
const DEFAULT_SECTIONS = [
  { id: 'hero', label: 'Keyword Hero' },
  { id: 'trustbar', label: 'Trust Bar' },
  { id: 'services', label: 'Leistungen' },
  { id: 'showcase', label: 'Visual Showcase' },
  { id: 'projects', label: 'Projekte' },
  { id: 'process', label: 'Prozess' },
  { id: 'testimonials', label: 'Kundenstimmen' },
  { id: 'faq', label: 'FAQ' },
  { id: 'cta', label: 'CTA' },
];

const DEFAULT_SECTION_SETTINGS = {
  visible: true,
  order: 0,
  paddingTop: 'medium',
  paddingBottom: 'medium',
  contentWidth: 'normal',
  preset: 'default',
  alignment: 'left',
  deviceVisibility: 'all',
  cta: { enabled: false, label: '', href: '' },
  animation: 'fade-up',
};

export { DEFAULT_SECTIONS, DEFAULT_SECTION_SETTINGS };

export function EditorProvider({ children }) {
  const [isActive, setIsActive] = useState(false);
  const [token, setToken] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [pending, setPending] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  /* ── Layout state ── */
  const [layout, setLayout] = useState({ sections: {}, imageDefaults: { fit: 'cover', aspect: 'auto' } });
  const [layoutPending, setLayoutPending] = useState(null);
  const [isLayoutSaving, setIsLayoutSaving] = useState(false);

  /* ── Shared fetch: always reads from public endpoint (single source of truth) ── */
  const refreshOverrides = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/content/overrides`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (!res.ok) return;
      const data = await res.json();
      setOverrides(data.overrides || {});
    } catch (err) {
      console.error('Failed to load content overrides:', err);
    }
  }, []);

  const refreshLayout = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/editor/layout?page=home`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (!res.ok) return;
      const data = await res.json();
      setLayout({
        sections: data.sections || {},
        imageDefaults: data.imageDefaults || { fit: 'cover', aspect: 'auto' },
      });
    } catch (err) {
      console.error('Failed to load layout:', err);
    }
  }, []);

  /* ── Restore session from storage ── */
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('vw_admin');
      if (stored) {
        const { token: t, active } = JSON.parse(stored);
        setToken(t);
        setIsActive(active);
      }
    } catch (err) {
      console.error('Failed to restore admin session:', err);
    }
  }, []);

  /* ── Initial load: single fetch for overrides + layout ── */
  useEffect(() => {
    let cancelled = false;
    async function load() {
      await Promise.all([refreshOverrides(), refreshLayout()]);
      if (!cancelled) setInitialLoadDone(true);
    }
    load();
    return () => { cancelled = true; };
  }, [refreshOverrides, refreshLayout]);

  const login = async (password) => {
    const res = await fetch(`${API}/api/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });
    if (!res.ok) throw new Error('Invalid password');
    const data = await res.json();
    setToken(data.token);
    sessionStorage.setItem('vw_admin', JSON.stringify({ token: data.token, active: false }));
    return true;
  };

  const activate = () => {
    setIsActive(true);
    sessionStorage.setItem('vw_admin', JSON.stringify({ token, active: true }));
  };

  const deactivate = () => {
    setIsActive(false);
    setPending({});
    setLayoutPending(null);
    sessionStorage.setItem('vw_admin', JSON.stringify({ token, active: false }));
    // Re-fetch from DB to ensure live view shows latest saved data
    refreshOverrides();
  };

  const logout = () => {
    setToken(null);
    setIsActive(false);
    setPending({});
    setLayoutPending(null);
    sessionStorage.removeItem('vw_admin');
    // Re-fetch from DB to ensure live view shows latest saved data
    refreshOverrides();
  };

  const getValue = useCallback(
    (key, fallback) => {
      if (key in pending) return pending[key];
      if (key in overrides) return overrides[key];
      return fallback;
    },
    [pending, overrides]
  );

  const setValue = useCallback((key, value) => {
    setPending((prev) => ({ ...prev, [key]: value }));
  }, []);

  const save = async (page) => {
    if (Object.keys(pending).length === 0) return true;
    setIsSaving(true);
    try {
      const entries = Object.entries(pending).map(([key, value]) => {
        const isImage =
          (typeof value === 'object' && value !== null && value.url) ||
          (typeof value === 'string' && /\.(jpe?g|png|webp|gif|svg)$/i.test(value));
        return { key, value, type: isImage ? 'image' : 'text', page: page || '' };
      });
      const res = await fetch(`${API}/api/admin/overrides`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ overrides: entries }),
      });
      if (!res.ok) throw new Error('Save failed');
      
      // Immediately update local state with pending values
      setOverrides(prev => ({ ...prev, ...pending }));
      setPending({});
      
      // Re-fetch from DB to confirm sync (single source of truth)
      await refreshOverrides();
      return true;
    } catch (err) {
      console.error('Save failed:', err);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  /* ── Layout helpers ── */
  const getEffectiveLayout = useCallback(() => {
    return layoutPending || layout;
  }, [layout, layoutPending]);

  const getSectionSettings = useCallback((sectionId) => {
    const effective = layoutPending || layout;
    const saved = effective.sections?.[sectionId];
    const idx = DEFAULT_SECTIONS.findIndex(s => s.id === sectionId);
    return { ...DEFAULT_SECTION_SETTINGS, order: idx >= 0 ? idx : 99, ...saved };
  }, [layout, layoutPending]);

  const updateSectionSetting = useCallback((sectionId, key, value) => {
    setLayoutPending(prev => {
      const base = prev || { ...layout };
      const sections = { ...base.sections };
      sections[sectionId] = { ...(sections[sectionId] || {}), [key]: value };
      return { ...base, sections };
    });
  }, [layout]);

  const updateImageDefaults = useCallback((key, value) => {
    setLayoutPending(prev => {
      const base = prev || { ...layout };
      const imageDefaults = { ...(base.imageDefaults || {}), [key]: value };
      return { ...base, imageDefaults };
    });
  }, [layout]);

  const reorderSection = useCallback((sectionId, direction) => {
    setLayoutPending(prev => {
      const base = prev || { ...layout };
      const sections = { ...base.sections };

      // Build ordered list
      const ordered = DEFAULT_SECTIONS.map(s => ({
        id: s.id,
        order: sections[s.id]?.order ?? DEFAULT_SECTIONS.findIndex(d => d.id === s.id),
      })).sort((a, b) => a.order - b.order);

      const idx = ordered.findIndex(s => s.id === sectionId);
      if (idx < 0) return base;
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= ordered.length) return base;

      // Swap orders
      const tempOrder = ordered[idx].order;
      ordered[idx].order = ordered[swapIdx].order;
      ordered[swapIdx].order = tempOrder;

      ordered.forEach(s => {
        sections[s.id] = { ...(sections[s.id] || {}), order: s.order };
      });

      return { ...base, sections };
    });
  }, [layout]);

  // New function for drag & drop reordering - accepts array of IDs in new order
  const reorderSections = useCallback((newOrderIds) => {
    setLayoutPending(prev => {
      const base = prev || { ...layout };
      const sections = { ...base.sections };

      // Assign new order values based on array position
      newOrderIds.forEach((id, index) => {
        sections[id] = { ...(sections[id] || {}), order: index };
      });

      return { ...base, sections };
    });
  }, [layout]);

  const duplicateSection = useCallback((sourceId, targetId) => {
    setLayoutPending(prev => {
      const base = prev || { ...layout };
      const sections = { ...base.sections };
      const sourceSettings = sections[sourceId] || {};
      const targetOrder = sections[targetId]?.order ?? DEFAULT_SECTIONS.findIndex(d => d.id === targetId);
      sections[targetId] = { ...sourceSettings, order: targetOrder };
      return { ...base, sections };
    });
  }, [layout]);

  const saveTemplate = async (name) => {
    const effective = layoutPending || layout;
    try {
      const res = await fetch(`${API}/api/admin/layout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          page: `template:${name}`,
          sections: effective.sections,
          imageDefaults: effective.imageDefaults,
        }),
      });
      return res.ok;
    } catch (err) { console.error('saveTemplate failed:', err); return false; }
  };

  const loadTemplate = async (name) => {
    try {
      const res = await fetch(`${API}/api/editor/layout?page=${encodeURIComponent(`template:${name}`)}`, {
        cache: 'no-store',
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (data.sections && Object.keys(data.sections).length > 0) {
        setLayoutPending({ sections: data.sections, imageDefaults: data.imageDefaults || { fit: 'cover', aspect: 'auto' } });
        return true;
      }
      return false;
    } catch (err) { console.error('loadTemplate failed:', err); return false; }
  };

  const listTemplates = async () => {
    try {
      const res = await fetch(`${API}/api/editor/templates`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) return [];
      const data = await res.json();
      return data.templates || [];
    } catch (err) { console.error('listTemplates failed:', err); return []; }
  };

  const deleteTemplate = async (name) => {
    try {
      const res = await fetch(`${API}/api/admin/template/${encodeURIComponent(name)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.ok;
    } catch (err) { console.error('deleteTemplate failed:', err); return false; }
  };

  const saveLayout = async () => {
    if (!layoutPending) return true;
    setIsLayoutSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/layout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          page: 'home',
          sections: layoutPending.sections,
          imageDefaults: layoutPending.imageDefaults,
        }),
      });
      if (!res.ok) throw new Error('Layout save failed');
      setLayout(layoutPending);
      setLayoutPending(null);
      // Re-fetch layout from DB to confirm sync
      await refreshLayout();
      return true;
    } catch (err) {
      console.error('Layout save failed:', err);
      return false;
    } finally {
      setIsLayoutSaving(false);
    }
  };

  const resetPage = async (page) => {
    try {
      await fetch(`${API}/api/admin/overrides?page=${encodeURIComponent(page)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      await refreshOverrides();
      setPending({});
    } catch (err) {
      console.error('Reset failed:', err);
    }
  };

  const resetAll = async () => {
    try {
      await fetch(`${API}/api/admin/overrides/all`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      await refreshOverrides();
      setPending({});
    } catch (err) {
      console.error('Reset all failed:', err);
    }
  };

  const compressImage = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxW = 1920;
          let { width, height } = img;
          if (width > maxW) {
            height = Math.round((height * maxW) / width);
            width = maxW;
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (blob) => blob ? resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.webp'), { type: 'image/webp' })) : reject(new Error('Compression failed')),
            'image/webp',
            0.82
          );
        };
        img.onerror = () => resolve(file);
        img.src = event.target.result;
      };
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    });
  };

  const uploadImage = async (file) => {
    let uploadFile = file;
    try {
      if (file.size > 500 * 1024) {
        uploadFile = await compressImage(file);
      }
    } catch (err) {
      console.error('Image compression failed, using original:', err);
      uploadFile = file;
    }
    const formData = new FormData();
    formData.append('file', uploadFile);
    const res = await fetch(`${API}/api/admin/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(detail || `Upload failed (${res.status})`);
    }
    const data = await res.json();
    return data.url;
  };

  return (
    <EditorContext.Provider
      value={{
        isActive,
        isLoggedIn: !!token,
        token,
        pending,
        overrides,
        isSaving,
        pendingCount: Object.keys(pending).length,
        login,
        logout,
        activate,
        deactivate,
        getValue,
        setValue,
        save,
        resetPage,
        resetAll,
        uploadImage,
        fetchOverrides: refreshOverrides,
        /* Layout */
        layout: getEffectiveLayout(),
        layoutPending,
        isLayoutSaving,
        getSectionSettings,
        updateSectionSetting,
        updateImageDefaults,
        reorderSection,
        reorderSections,
        duplicateSection,
        saveLayout,
        hasLayoutChanges: !!layoutPending,
        /* Templates */
        saveTemplate,
        loadTemplate,
        listTemplates,
        deleteTemplate,
      }}
    >
      {children}
    </EditorContext.Provider>
  );
}

export function useEditor() {
  return useContext(EditorContext) || {};
}

export function useEditable(key, fallback) {
  const ctx = useContext(EditorContext);
  if (!ctx) return fallback;
  return ctx.getValue(key, fallback);
}
